import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";

const sanitizeId = (value, fallback) =>
  String(value || fallback)
    .trim()
    .replace(/[/.#?]/g, "-");

export const createReferenceAuditSnapshot = (value) => {
  if (value === undefined || value === null) return null;
  if (["string", "number", "boolean"].includes(typeof value)) return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === "function") return value.toDate().toISOString();
  if (typeof File !== "undefined" && value instanceof File) {
    return {
      name: value.name,
      size: value.size,
      type: value.type || "application/octet-stream",
      lastModified: value.lastModified,
    };
  }
  if (Array.isArray(value)) return value.map(createReferenceAuditSnapshot);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, createReferenceAuditSnapshot(item)]),
    );
  }
  return String(value);
};

const valuesEqual = (previousValue, newValue) =>
  JSON.stringify(createReferenceAuditSnapshot(previousValue)) ===
  JSON.stringify(createReferenceAuditSnapshot(newValue));

const getChanges = (before = {}, after = {}, path = "") => {
  const previous = before && typeof before === "object" ? before : {};
  const next = after && typeof after === "object" ? after : {};
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);

  return [...keys].flatMap((key) => {
    const fieldPath = path ? `${path}.${key}` : key;
    const previousValue = previous[key];
    const newValue = next[key];
    const bothObjects =
      previousValue &&
      newValue &&
      !Array.isArray(previousValue) &&
      !Array.isArray(newValue) &&
      typeof previousValue === "object" &&
      typeof newValue === "object" &&
      !(previousValue instanceof Date) &&
      !(newValue instanceof Date) &&
      typeof previousValue?.toDate !== "function" &&
      typeof newValue?.toDate !== "function";

    if (bothObjects) return getChanges(previousValue, newValue, fieldPath);
    if (valuesEqual(previousValue, newValue)) return [];

    return [{
      fieldPath,
      previousValue: createReferenceAuditSnapshot(previousValue),
      newValue: createReferenceAuditSnapshot(newValue),
    }];
  });
};

export const logReferenceAudit = async ({
  applicationId,
  referenceId,
  referenceKind,
  action,
  before = {},
  after = {},
  details = {},
  changes: suppliedChanges,
}) => {
  const user = auth.currentUser;
  if (!user?.uid) throw new Error("A logged-in user is required for reference auditing.");
  if (!applicationId || !referenceId || !action) {
    throw new Error("Application ID, reference ID and audit action are required.");
  }

  const changes = (suppliedChanges || getChanges(before, after)).map((change) => ({
    fieldPath: change.fieldPath,
    previousValue: createReferenceAuditSnapshot(change.previousValue),
    newValue: createReferenceAuditSnapshot(change.newValue),
  }));
  if (!changes.length && action === "reference_saved") return null;

  const updatedBy = user.email || "";
  const updatedByName = user.displayName || updatedBy || "Unknown user";

  return addDoc(
    collection(
      db,
      "Recruitment",
      sanitizeId(applicationId, "unknown-application"),
      "References",
      sanitizeId(referenceId, "unknown-reference"),
      "Edit Logs",
    ),
    {
      applicationId: String(applicationId),
      referenceId: String(referenceId),
      referenceKind: referenceKind || "work-history",
      action,
      changes,
      changeCount: changes.length,
      details: createReferenceAuditSnapshot(details),
      updatedAt: serverTimestamp(),
      updatedBy,
      updatedByName,
      editedAt: serverTimestamp(),
      editedBy: updatedBy,
      editedByUid: user.uid,
    },
  );
};
